/**
 * Professional Thermal Receipt Builder
 * Generates ESC/POS commands for branded, beautiful receipts
 * with QR Code, restaurant identity, and organized layout.
 *
 * Supports: Epson, Star, Bixolon, and most ESC/POS compatible printers
 * Paper widths: 80mm (42-48 cols) and 58mm (32 cols)
 * Languages: ES, PT, EN (based on system configuration)
 */

import { getReceiptTranslations, getSourceLabel, getCurrentLanguage } from './receiptTranslations';
import { getItemComponents, getComponentsDescription } from './receiptComponents';

// ─── ESC/POS Constants ────────────────────────────────
const ESC = 0x1B;
const GS  = 0x1D;
const LF  = 0x0A;

// ─── Helpers ──────────────────────────────────────────
function textEncode(str) {
  return new TextEncoder().encode(str);
}

function center(text, cols) {
  const pad = Math.max(0, Math.floor((cols - text.length) / 2));
  return " ".repeat(pad) + text;
}

function leftRight(left, right, cols) {
  const spaces = Math.max(1, cols - left.length - right.length);
  return left + " ".repeat(spaces) + right;
}

function wrapText(text, cols) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    if ((line + " " + w).trim().length > cols) {
      if (line) lines.push(line);
      line = w;
    } else {
      line = line ? line + " " + w : w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function formatCurrency(value, currency) {
  const num = (value || 0).toFixed(2);
  const sym = { eur: "\u20AC", usd: "$", brl: "R$", gbp: "\u00A3" }[currency] || "\u20AC";
  return num + sym;
}

function formatDate(dateStr, lang) {
  const locale = lang === 'es' ? 'es-ES' : lang === 'pt' ? 'pt-BR' : 'en-US';
  try {
    const d = dateStr ? new Date(dateStr) : new Date();
    return d.toLocaleString(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return new Date().toLocaleString(locale);
  }
}

// ─── QR Code ESC/POS Builder ──────────────────────────
function buildQRCodeBytes(url, moduleSize) {
  if (!url) return [];
  const size = moduleSize || 5;
  const urlBytes = textEncode(url);
  const storeLen = urlBytes.length + 3;
  const pL = storeLen & 0xFF;
  const pH = (storeLen >> 8) & 0xFF;

  return [
    // Select QR model 2
    GS, 0x28, 0x6B, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00,
    // Set module size
    GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x43, size,
    // Error correction level M
    GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x45, 0x31,
    // Store data
    GS, 0x28, 0x6B, pL, pH, 0x31, 0x50, 0x30, ...urlBytes,
    // Print QR code
    GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x51, 0x30,
  ];
}

// ─── String-based QR code (for QZ Tray raw string mode) ──
function buildQRCodeString(url, moduleSize) {
  if (!url) return "";
  const size = moduleSize || 5;
  const storeLen = url.length + 3;
  const pL = String.fromCharCode(storeLen & 0xFF);
  const pH = String.fromCharCode((storeLen >> 8) & 0xFF);
  const s = String.fromCharCode;
  const _GS = s(0x1D);

  return (
    _GS + "(k" + s(4) + s(0) + "1A2" + s(0) +       // Model 2
    _GS + "(k" + s(3) + s(0) + "1C" + s(size) +      // Module size
    _GS + "(k" + s(3) + s(0) + "1E1" +                // Error correction M
    _GS + "(k" + pL + pH + "1P0" + url +              // Store data
    _GS + "(k" + s(3) + s(0) + "1Q0"                  // Print
  );
}

// ─── Decorative line builders ─────────────────────────
function doubleLine(cols) { return "\u2550".repeat(cols); }
function thinLine(cols)   { return "\u2500".repeat(cols); }
// Fallback for printers that don't support unicode
function solidLine(cols)  { return "=".repeat(cols); }
function dashLine(cols)   { return "-".repeat(cols); }
function starLine(cols)   { return "*".repeat(cols); }
function dotLine(cols)    { return ".".repeat(cols); }

// ─── IVA / Tax breakdown (four-level fallback) ────────
/**
 * Resolve default VAT rate from restaurant config.
 * Priority: fiscal_config.default_vat_rate → restaurant.tax_rate → country default → 10% fallback
 */
function _resolveRestaurantTaxRate(restaurant) {
  const COUNTRY_DEFAULTS = {
    ES: 10, PT: 23, FR: 20, DE: 19, IT: 22, GB: 20, MX: 16, AE: 5, SA: 15, AR: 21, CL: 19, CO: 19, MA: 20,
  };
  const fc = restaurant.fiscal_config;
  if (fc?.default_vat_rate != null) return Number(fc.default_vat_rate);
  if (restaurant.tax_rate != null) return Number(restaurant.tax_rate);
  const country = (restaurant.country || "ES").toUpperCase();
  return COUNTRY_DEFAULTS[country] ?? 10;
}

/**
 * Compute IVA breakdown for receipt display.
 *   1. order.vendus_taxes  — from Vendus fiscal API (most accurate)
 *   2. order.tax_snapshot  — frozen at order creation time (reprints)
 *   3. order.tax_summary   — pre-computed and stored by backend
 *   4. Computed from order.items using restaurant config rate
 *
 * @param {Object} order
 * @param {Object} [restaurant={}] - Restaurant config for tax rate resolution
 * @param {number} [defaultRate]   - Override rate (uses restaurant config when omitted)
 * @returns {{ lines: Array<{rate,base,amount}>, tax_base: number, tax_amount: number }}
 */
function _computeReceiptTax(order, restaurant = {}, defaultRate) {
  if (defaultRate === undefined) defaultRate = _resolveRestaurantTaxRate(restaurant);

  // Level 1: Vendus-computed taxes
  const vendus_taxes = order.vendus_taxes;
  if (Array.isArray(vendus_taxes) && vendus_taxes.length > 0) {
    const lines = [];
    let tax_base = 0;
    let tax_amount = 0;
    for (const tx of vendus_taxes) {
      const rate   = tx.rate || tx.percent || tx.vat_rate || 0;
      const amount = tx.value || tx.tax_value || 0;
      const base   = tx.base_value || tx.net_value || tx.subtotal || 0;
      if (amount > 0 || base > 0) {
        lines.push({ rate, base, amount });
        tax_base   += base;
        tax_amount += amount;
      }
    }
    if (lines.length > 0) return { lines, tax_base, tax_amount };
  }

  // Level 2: order tax snapshot (frozen at creation, preserved for reprints)
  const snapshot = order.tax_snapshot;
  if (snapshot?.tax_summary?.lines?.length > 0 && snapshot.tax_summary.tax_amount > 0) {
    return {
      lines:      snapshot.tax_summary.lines.map(ln => ({ rate: ln.rate, base: ln.base, amount: ln.amount })),
      tax_base:   snapshot.tax_summary.tax_base   || 0,
      tax_amount: snapshot.tax_summary.tax_amount || 0,
    };
  }

  // Level 3: backend pre-computed tax_summary
  const ts = order.tax_summary;
  if (ts && Array.isArray(ts.lines) && ts.lines.length > 0 && ts.tax_amount > 0) {
    return {
      lines:      ts.lines.map(ln => ({ rate: ln.rate, base: ln.base, amount: ln.amount })),
      tax_base:   ts.tax_base   || 0,
      tax_amount: ts.tax_amount || 0,
    };
  }

  // Level 4: compute from order.items (prices include tax)
  const items = order.items || [];
  const grossByRate = {};
  for (const item of items) {
    const rate  = item.tax_rate ?? item.vat_rate ?? defaultRate;
    const qty   = item.quantity  || 1;
    const price = item.unit_price || item.price || 0;
    grossByRate[rate] = (grossByRate[rate] || 0) + qty * price;
  }

  const lines = [];
  let tax_base   = 0;
  let tax_amount = 0;

  for (const rateStr of Object.keys(grossByRate).sort((a, b) => Number(a) - Number(b))) {
    const rate   = Number(rateStr);
    const gross  = grossByRate[rate];
    const base   = Math.round(gross / (1 + rate / 100) * 100) / 100;
    const amount = Math.round((gross - base) * 100) / 100;
    lines.push({ rate, base, amount });
    tax_base   += base;
    tax_amount += amount;
  }

  return {
    lines,
    tax_base:   Math.round(tax_base   * 100) / 100,
    tax_amount: Math.round(tax_amount * 100) / 100,
  };
}

/**
 * Build a beautiful Portuguese fiscal invoice receipt (FATURA) for thermal printer.
 *
 * @param {object} invoiceData  - { invoice_number, atcud, qr_code, doc_date,
 *                                  customer:{name,nif}, items, payment_method,
 *                                  discount_amount, total_gross, total_tax, taxes }
 * @param {object} restaurant   - { name, address, phone, fiscal_id, currency, receipt_footer }
 * @param {object} opts         - { columns:42, language:'pt' }
 */
export function buildFiscalReceiptString(invoiceData, restaurant, opts = {}) {
  const cols = opts.columns || 42;
  const lang = opts.language || "pt";
  const currency = (restaurant.currency || "eur").toLowerCase();

  const ESC = "\x1B";
  const GS  = "\x1D";
  const LF  = "\n";
  const BOLD_ON  = ESC + "E\x01";
  const BOLD_OFF = ESC + "E\x00";
  const CENTER   = ESC + "a\x01";
  const LEFT     = ESC + "a\x00";
  const DBL      = GS + "!\x11";  // double width+height
  const DBL_H    = GS + "!\x01";  // double height only
  const NORM     = GS + "!\x00";
  const CUT      = GS + "VA\x03";

  const solid = solidLine(cols);
  const dash  = dashLine(cols);

  const {
    invoice_number, atcud, qr_code, doc_date,
    customer = {}, payment_method,
    total_gross, total_tax, taxes = [],
    discount_amount = 0,
  } = invoiceData;
  const items = invoiceData.items || [];

  let s = "";

  // ── HEADER ─────────────────────────────────────────────
  // CENTER mode is set first; in double-width (21 cols) center() would add 15 spaces
  // and wrap "KAISO SUSHI" across two lines — send raw name, hardware does the centering.
  s += CENTER;
  s += DBL + BOLD_ON + (restaurant.name || "Restaurante").toUpperCase() + LF + NORM + BOLD_OFF;
  if (restaurant.address) s += center(restaurant.address, cols) + LF;
  if (restaurant.phone)   s += center("Tel: " + restaurant.phone, cols) + LF;
  const nif = restaurant.fiscal_id || restaurant.cif || "";
  if (nif) s += center("NIF: " + nif, cols) + LF;

  s += LEFT + solid + LF;

  // ── DOCUMENT TYPE ───────────────────────────────────────
  s += CENTER + DBL_H + BOLD_ON + center("FATURA", cols) + LF + NORM + BOLD_OFF;
  s += CENTER + BOLD_ON + center(invoice_number || "", cols) + LF + BOLD_OFF;

  // ── FISCAL DATA ─────────────────────────────────────────
  s += LEFT + dash + LF;
  s += leftRight("Data:", formatDate(doc_date, lang), cols) + LF;
  if (atcud) s += leftRight("ATCUD:", atcud, cols) + LF;

  // ── CUSTOMER ────────────────────────────────────────────
  if (customer.name || customer.nif) {
    s += dash + LF;
    if (customer.name) s += leftRight("Cliente:", (customer.name || "").substring(0, cols - 10), cols) + LF;
    if (customer.nif)  s += leftRight("NIF:", customer.nif, cols) + LF;
  }

  s += solid + LF;

  // ── ITEMS ───────────────────────────────────────────────
  s += CENTER + BOLD_ON + center("ARTIGOS", cols) + LF + BOLD_OFF;
  s += LEFT + dash + LF;

  for (const item of items) {
    const qty   = item.quantity || 1;
    const price = item.unit_price || 0;
    const line  = qty * price;
    const name  = (item.product_name || item.name || "Item").substring(0, cols - 14);

    s += BOLD_ON + qty + "x " + name + BOLD_OFF + LF;
    s += leftRight("  " + formatCurrency(price, currency) + "/un", formatCurrency(line, currency), cols) + LF;

    const compDesc = getComponentsDescription(item);
    if (compDesc) s += "  + " + compDesc.substring(0, cols - 4) + LF;
    if (item.notes) s += "  * " + item.notes.substring(0, cols - 4) + LF;
  }

  s += dash + LF;

  // ── TOTALS ──────────────────────────────────────────────
  const rawSubtotal = items.reduce((acc, i) => acc + (i.quantity || 1) * (i.unit_price || 0), 0);
  const subtotal = total_gross != null ? total_gross : rawSubtotal;
  const tax  = total_tax || 0;
  const total = total_gross || Math.max(0, rawSubtotal - discount_amount);

  s += leftRight("Subtotal:", formatCurrency(rawSubtotal, currency), cols) + LF;
  if (discount_amount > 0) s += leftRight("Desconto:", "-" + formatCurrency(discount_amount, currency), cols) + LF;
  if (tax > 0) {
    if (taxes.length > 0) {
      for (const tx of taxes) {
        if ((tx.value || tx.tax_value || 0) > 0) {
          const rate = tx.rate || tx.percent || tx.vat_rate || "";
          const val  = tx.value || tx.tax_value || 0;
          s += leftRight("  IVA " + (rate ? rate + "%" : ""), formatCurrency(val, currency), cols) + LF;
        }
      }
    } else {
      s += leftRight("IVA incluido:", formatCurrency(tax, currency), cols) + LF;
    }
  }

  s += solid + LF;

  // TOTAL — large
  s += CENTER + DBL + BOLD_ON + center("TOTAL " + formatCurrency(total, currency), cols) + LF + NORM + BOLD_OFF;
  s += LEFT + solid + LF;

  // ── PAYMENT ─────────────────────────────────────────────
  if (payment_method) {
    s += BOLD_ON + leftRight("Pagamento:", payment_method.toUpperCase(), cols) + LF + BOLD_OFF;
  }

  // ── AT QR CODE ──────────────────────────────────────────
  if (qr_code) {
    s += LF;
    s += CENTER + dash + LF;
    s += center("DOCUMENTO FISCAL AT", cols) + LF + LF;
    s += buildQRCodeString(qr_code, 4);
    s += LF + LF + LEFT;
  }

  // ── MENU QR CODE (same as normal receipt) ───────────────
  const _menuQrUrl = restaurant.menu_url ||
    (restaurant.custom_domain && restaurant.slug
      ? `https://${restaurant.custom_domain}/m/${restaurant.slug}`
      : restaurant.slug ? `https://kaisosystem.com/menu/${restaurant.slug}` : "");
  if (_menuQrUrl && restaurant.show_qr_code !== false) {
    s += LF;
    s += CENTER + dash + LF;
    s += center(restaurant.qr_message || "Escaneie e veja nosso menu", cols) + LF + LF;
    s += buildQRCodeString(_menuQrUrl, restaurant.qr_size || 5);
    s += LF + LF + LEFT;
  }

  // ── FOOTER ──────────────────────────────────────────────
  s += solid + LF;
  s += CENTER;
  const footer = restaurant.receipt_footer || "Obrigado pela preferencia!";
  for (const fl of wrapText(footer, cols)) s += center(fl, cols) + LF;
  s += LF + LF + LF + LEFT;
  s += CUT;

  return s;
}

/**
 * Build a professional receipt as ESC/POS STRING (for QZ Tray RAW mode)
 *
 * @param {Object} order - Order data
 * @param {Object} restaurant - Restaurant branding config
 * @param {Object} options - Print options
 * @returns {string} ESC/POS command string
 */
export function buildProfessionalReceiptString(order, restaurant = {}, options = {}) {
  const cols = options.columns || 42;
  const currency = restaurant.currency || options.currency || "eur";
  const lang = options.language || restaurant.language || getCurrentLanguage();
  const t = getReceiptTranslations(lang);
  const lines = [];

  const _ESC = "\x1B";
  const _GS = "\x1D";

  // Initialize printer
  lines.push(_ESC + "@");

  // ══════════════════════════════════════════
  //  HEADER - Restaurant Identity
  // ══════════════════════════════════════════
  lines.push(_ESC + "a\x01"); // Center

  // Restaurant name - DOUBLE SIZE, BOLD; hardware CENTER already set → no software padding needed
  const rName = (restaurant.name || "Restaurante").toUpperCase();
  lines.push(_GS + "!\x11"); // Double width + height
  lines.push(_ESC + "E\x01"); // Bold
  lines.push(rName + "\n");
  lines.push(_ESC + "E\x00"); // Bold off
  lines.push(_GS + "!\x00"); // Normal size

  // Address
  if (restaurant.address) {
    lines.push(restaurant.address + "\n");
  }
  // Phone
  if (restaurant.phone) {
    lines.push(t.phone + ": " + restaurant.phone + "\n");
  }
  // CIF/NIF
  if (restaurant.cif) {
    lines.push(t.nif + ": " + restaurant.cif + "\n");
  }

  lines.push(solidLine(cols) + "\n");

  // ══════════════════════════════════════════
  //  ORDER INFO
  // ══════════════════════════════════════════
  lines.push(_ESC + "a\x00"); // Left align

  // Order number - BOLD, slightly larger
  const orderNum = (order.order_number || order.id || "").toString().slice(-6).toUpperCase();
  lines.push(_ESC + "E\x01");
  lines.push(_GS + "!\x01"); // Double height
  lines.push(t.orderNumber + " #" + orderNum + "\n");
  lines.push(_GS + "!\x00");
  lines.push(_ESC + "E\x00");

  // Date
  lines.push(t.date + ": " + formatDate(order.created_at, lang) + "\n");

  // Customer
  if (order.customer_name) {
    lines.push(t.customer + ": " + order.customer_name + "\n");
  }
  if (order.customer_phone) {
    lines.push(t.phone + ": " + order.customer_phone + "\n");
  }

  // Channel
  const source = order.source || order.channel;
  if (source) {
    lines.push(t.channel + ": " + getSourceLabel(source, lang) + "\n");
  }

  // Table
  if (order.table_number) {
    lines.push(t.table + ": " + order.table_number + "\n");
  }

  // Delivery address
  if (order.delivery_address) {
    lines.push(t.delivery + ": " + order.delivery_address + "\n");
  }

  lines.push(solidLine(cols) + "\n");

  // ══════════════════════════════════════════
  //  ITEMS
  // ══════════════════════════════════════════
  lines.push(_ESC + "a\x01"); // Center
  lines.push(_ESC + "E\x01");
  lines.push(t.orderItems + "\n");
  lines.push(_ESC + "E\x00");
  lines.push(_ESC + "a\x00"); // Left
  lines.push(dashLine(cols) + "\n");

  const items = order.items || [];
  for (const item of items) {
    const qty = item.quantity || 1;
    const price = item.unit_price || item.price || 0;
    const total = qty * price;
    const name = (item.product_name || item.name || "Item").substring(0, cols - 4);
    const totalStr = formatCurrency(total, currency);

    // Item name with quantity (bold)
    lines.push(_ESC + "E\x01");
    lines.push(qty + "x " + name + "\n");
    lines.push(_ESC + "E\x00");

    // Price aligned right
    lines.push(leftRight("", totalStr, cols) + "\n");

    // Modifiers (POS) or selected_variations (public menu) — unified
    for (const comp of getItemComponents(item)) {
      const compPrice = comp.price ? " +" + formatCurrency(comp.price, currency) : "";
      lines.push("  + " + comp.label + compPrice + "\n");
    }

    // Notes/observations
    if (item.notes || item.observations || item.comment) {
      const note = item.notes || item.observations || item.comment;
      lines.push("  * " + note.substring(0, cols - 4) + "\n");
    }
  }

  lines.push(dashLine(cols) + "\n");

  // ══════════════════════════════════════════
  //  TOTALS
  // ══════════════════════════════════════════

  // Subtotal
  const subtotal = order.subtotal || items.reduce((s, i) => s + (i.quantity || 1) * (i.unit_price || i.price || 0), 0);
  lines.push(leftRight(t.subtotal + ":", formatCurrency(subtotal, currency), cols) + "\n");

  // Discount (backend stores as discount_amount; discount is a legacy alias)
  const discountValue = order.discount_amount ?? order.discount ?? 0;
  if (discountValue > 0) {
    const discLabel = (order.discount_type === "percent" && order.discount_value > 0)
      ? `${t.discount} ${order.discount_value}%:`
      : `${t.discount}:`;
    lines.push(leftRight(discLabel, "-" + formatCurrency(discountValue, currency), cols) + "\n");
  }

  // IVA breakdown — always shown via three-level fallback (vendus_taxes → tax_summary → computed)
  const _taxResult = _computeReceiptTax(order, restaurant);
  if (_taxResult.lines.length > 0 && _taxResult.tax_amount > 0) {
    lines.push(leftRight(t.taxBase + ":", formatCurrency(_taxResult.tax_base, currency), cols) + "\n");
    for (const ln of _taxResult.lines) {
      lines.push(leftRight(t.tax + " " + ln.rate + "%:", formatCurrency(ln.amount, currency), cols) + "\n");
    }
  }

  // Delivery fee
  if (order.delivery_fee && order.delivery_fee > 0) {
    lines.push(leftRight(t.deliveryFee + ":", formatCurrency(order.delivery_fee, currency), cols) + "\n");
  }

  // Service charge
  if (order.service_charge && order.service_charge > 0) {
    lines.push(leftRight(t.serviceCharge + ":", formatCurrency(order.service_charge, currency), cols) + "\n");
  }

  lines.push(solidLine(cols) + "\n");

  // TOTAL - DOUBLE SIZE BOLD
  lines.push(_ESC + "a\x01"); // Center
  lines.push(_ESC + "E\x01");
  lines.push(_GS + "!\x11"); // Double width + height
  lines.push(t.total + " " + formatCurrency(order.total, currency) + "\n");
  lines.push(_GS + "!\x00");
  lines.push(_ESC + "E\x00");
  lines.push(_ESC + "a\x00"); // Left

  // PERSONAS / IMPORTE POR PERSONA (visual only - guest count display)
  if (order.guests && order.guests > 0) {
    lines.push("\n");
    lines.push(leftRight("PERSONAS:", String(order.guests), cols) + "\n");
    if (order.per_person !== undefined && order.per_person > 0) {
      lines.push(leftRight("IMPORTE POR PERSONA:", formatCurrency(order.per_person, currency), cols) + "\n");
    }
  }

  lines.push(solidLine(cols) + "\n");

  // Payment method
  if (order.payment_method) {
    lines.push(_ESC + "E\x01");
    lines.push(leftRight(t.payment + ":", (order.payment_method || "").toUpperCase(), cols) + "\n");
    lines.push(_ESC + "E\x00");
  }

  // Change
  if (order.change_amount && order.change_amount > 0) {
    lines.push(leftRight(t.change + ":", formatCurrency(order.change_amount, currency), cols) + "\n");
  }

  // Order notes
  if (order.notes || order.observations) {
    lines.push("\n");
    lines.push(_ESC + "E\x01");
    lines.push(t.notes + ": ");
    lines.push(_ESC + "E\x00");
    const noteLines = wrapText(order.notes || order.observations, cols - 2);
    for (const nl of noteLines) {
      lines.push(nl + "\n");
    }
  }

  lines.push("\n");

  // ══════════════════════════════════════════
  //  QR CODE
  // ══════════════════════════════════════════
  // Prefer explicit menu_url → custom domain → fallback to kaisosystem.com generic
  const menuUrl = restaurant.menu_url ||
    (restaurant.custom_domain && restaurant.slug
      ? `https://${restaurant.custom_domain}/m/${restaurant.slug}`
      : restaurant.slug ? `https://kaisosystem.com/menu/${restaurant.slug}` : "");
  const showQR = restaurant.show_qr_code !== false && menuUrl;

  if (showQR) {
    lines.push(_ESC + "a\x01"); // Center
    lines.push(dashLine(cols) + "\n");
    lines.push("\n");
    lines.push((restaurant.qr_message || t.scanQR) + "\n");
    lines.push("\n");

    // QR Code
    lines.push(buildQRCodeString(menuUrl, restaurant.qr_size || 6));
    lines.push("\n\n");

    lines.push(_ESC + "a\x00"); // Left
  }

  // ══════════════════════════════════════════
  //  FOOTER
  // ══════════════════════════════════════════
  lines.push(solidLine(cols) + "\n");
  lines.push(_ESC + "a\x01"); // Center

  // Custom footer message
  const footerMsg = restaurant.receipt_footer || restaurant.footer_message || t.thankYou;
  const footerLines = wrapText(footerMsg, cols);
  for (const fl of footerLines) {
    lines.push(fl + "\n");
  }

  // Social links
  if (restaurant.instagram_url || restaurant.instagram) {
    const ig = restaurant.instagram_url || restaurant.instagram;
    const handle = ig.includes("instagram.com/") ? "@" + ig.split("instagram.com/")[1].replace(/\//g, "") : ig;
    lines.push(handle + "\n");
  }

  if (restaurant.whatsapp_phone) {
    lines.push("WhatsApp: " + restaurant.whatsapp_phone + "\n");
  }

  lines.push("\n");
  lines.push(_ESC + "a\x00"); // Left

  // Feed and cut
  lines.push("\n\n\n");
  lines.push(_GS + "V\x00"); // Full cut

  return lines.join("");
}


/**
 * Build a professional receipt as Uint8Array (for Web Serial)
 *
 * @param {Object} order - Order data
 * @param {Object} restaurant - Restaurant branding config
 * @param {Object} options - Print options
 * @returns {Uint8Array} ESC/POS byte array
 */
export function buildProfessionalReceiptBytes(order, restaurant = {}, options = {}) {
  const cols = options.columns || 32;
  const currency = restaurant.currency || options.currency || "eur";
  const lang = options.language || restaurant.language || getCurrentLanguage();
  const t = getReceiptTranslations(lang);
  const chunks = [];

  const add = (bytes) => chunks.push(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes));
  const text = (txt) => add(textEncode(txt));
  const nl = () => add([LF]);
  const cmd = (...bytes) => add(bytes);

  // Initialize
  cmd(ESC, 0x40);

  // ── HEADER ──────────────────────────────
  cmd(ESC, 0x61, 0x01); // Center

  // Restaurant name - double size bold
  const rName = (restaurant.name || "Restaurante").toUpperCase();
  cmd(GS, 0x21, 0x11);  // Double width+height
  cmd(ESC, 0x45, 0x01); // Bold
  text(rName); nl();
  cmd(ESC, 0x45, 0x00);
  cmd(GS, 0x21, 0x00);  // Normal

  if (restaurant.address) { text(restaurant.address); nl(); }
  if (restaurant.phone)   { text(t.phone + ": " + restaurant.phone); nl(); }
  if (restaurant.cif)     { text(t.nif + ": " + restaurant.cif); nl(); }

  text(solidLine(cols)); nl();

  // ── ORDER INFO ──────────────────────────
  cmd(ESC, 0x61, 0x00); // Left

  const orderNum = (order.order_number || order.id || "").toString().slice(-6).toUpperCase();
  cmd(ESC, 0x45, 0x01);
  cmd(GS, 0x21, 0x01); // Double height
  text(t.orderNumber + " #" + orderNum); nl();
  cmd(GS, 0x21, 0x00);
  cmd(ESC, 0x45, 0x00);

  text(t.date + ": " + formatDate(order.created_at, lang)); nl();
  if (order.customer_name) { text(t.customer + ": " + order.customer_name); nl(); }
  if (order.customer_phone) { text(t.phone + ": " + order.customer_phone); nl(); }

  const source = order.source || order.channel;
  if (source) { text(t.channel + ": " + getSourceLabel(source, lang)); nl(); }
  if (order.table_number) { text(t.table + ": " + order.table_number); nl(); }
  if (order.delivery_address) { text(t.delivery + ": " + order.delivery_address); nl(); }

  text(solidLine(cols)); nl();

  // ── ITEMS ───────────────────────────────
  cmd(ESC, 0x61, 0x01); // Center
  cmd(ESC, 0x45, 0x01);
  text(t.orderItems); nl();
  cmd(ESC, 0x45, 0x00);
  cmd(ESC, 0x61, 0x00); // Left
  text(dashLine(cols)); nl();

  const items = order.items || [];
  for (const item of items) {
    const qty = item.quantity || 1;
    const price = item.unit_price || item.price || 0;
    const total = qty * price;
    const name = (item.product_name || item.name || "Item").substring(0, cols - 4);
    const totalStr = formatCurrency(total, currency);

    cmd(ESC, 0x45, 0x01);
    text(qty + "x " + name); nl();
    cmd(ESC, 0x45, 0x00);
    text(leftRight("", totalStr, cols)); nl();

    for (const comp of getItemComponents(item)) {
      const compPrice = comp.price ? " +" + formatCurrency(comp.price, currency) : "";
      text("  + " + comp.label + compPrice); nl();
    }
    if (item.notes || item.observations || item.comment) {
      text("  * " + (item.notes || item.observations || item.comment).substring(0, cols - 4)); nl();
    }
  }

  text(dashLine(cols)); nl();

  // ── TOTALS ──────────────────────────────
  const subtotal = order.subtotal || items.reduce((s, i) => s + (i.quantity || 1) * (i.unit_price || i.price || 0), 0);
  text(leftRight(t.subtotal + ":", formatCurrency(subtotal, currency), cols)); nl();

  const discountValueBytes = order.discount_amount ?? order.discount ?? 0;
  if (discountValueBytes > 0) {
    const discLabelBytes = (order.discount_type === "percent" && order.discount_value > 0)
      ? `${t.discount} ${order.discount_value}%:`
      : `${t.discount}:`;
    text(leftRight(discLabelBytes, "-" + formatCurrency(discountValueBytes, currency), cols)); nl();
  }
  if (order.tax > 0) { text(leftRight(t.tax + ":", formatCurrency(order.tax, currency), cols)); nl(); }
  if (order.delivery_fee > 0) { text(leftRight(t.deliveryFee + ":", formatCurrency(order.delivery_fee, currency), cols)); nl(); }
  if (order.service_charge > 0) { text(leftRight(t.serviceCharge + ":", formatCurrency(order.service_charge, currency), cols)); nl(); }

  text(solidLine(cols)); nl();

  // TOTAL - big
  cmd(ESC, 0x61, 0x01); // Center
  cmd(ESC, 0x45, 0x01);
  cmd(GS, 0x21, 0x11); // Double
  text(t.total + " " + formatCurrency(order.total, currency)); nl();
  cmd(GS, 0x21, 0x00);
  cmd(ESC, 0x45, 0x00);
  cmd(ESC, 0x61, 0x00);

  text(solidLine(cols)); nl();

  // Payment
  if (order.payment_method) {
    cmd(ESC, 0x45, 0x01);
    text(leftRight(t.payment + ":", order.payment_method.toUpperCase(), cols)); nl();
    cmd(ESC, 0x45, 0x00);
  }
  if (order.change_amount > 0) {
    text(leftRight(t.change + ":", formatCurrency(order.change_amount, currency), cols)); nl();
  }

  // Notes
  if (order.notes || order.observations) {
    nl();
    cmd(ESC, 0x45, 0x01); text(t.notes + ": "); cmd(ESC, 0x45, 0x00);
    for (const line of wrapText(order.notes || order.observations, cols - 2)) {
      text(line); nl();
    }
  }

  nl();

  // ── QR CODE ─────────────────────────────
  // Prefer explicit menu_url → custom domain → fallback to kaisosystem.com generic
  const menuUrl = restaurant.menu_url ||
    (restaurant.custom_domain && restaurant.slug
      ? `https://${restaurant.custom_domain}/m/${restaurant.slug}`
      : restaurant.slug ? `https://kaisosystem.com/menu/${restaurant.slug}` : "");
  const showQR = restaurant.show_qr_code !== false && menuUrl;

  if (showQR) {
    cmd(ESC, 0x61, 0x01); // Center
    text(dashLine(cols)); nl();
    nl();
    text(restaurant.qr_message || t.scanQR); nl();
    nl();

    const qrBytes = buildQRCodeBytes(menuUrl, restaurant.qr_size || 5);
    add(qrBytes);
    nl(); nl();

    cmd(ESC, 0x61, 0x00);
  }

  // ── FOOTER ──────────────────────────────
  text(solidLine(cols)); nl();
  cmd(ESC, 0x61, 0x01); // Center

  const footerMsg = restaurant.receipt_footer || restaurant.footer_message || t.thankYou;
  for (const fl of wrapText(footerMsg, cols)) { text(fl); nl(); }

  if (restaurant.instagram_url || restaurant.instagram) {
    const ig = restaurant.instagram_url || restaurant.instagram;
    const handle = ig.includes("instagram.com/") ? "@" + ig.split("instagram.com/")[1].replace(/\//g, "") : ig;
    text(handle); nl();
  }
  if (restaurant.whatsapp_phone) { text("WhatsApp: " + restaurant.whatsapp_phone); nl(); }

  nl();
  cmd(ESC, 0x61, 0x00);

  // Feed & cut
  cmd(ESC, 0x64, 4);        // Feed 4 lines
  cmd(GS, 0x56, 0x41, 0x03); // Partial cut

  // Combine chunks
  const totalLen = chunks.reduce((s, c) => s + c.length, 0);
  const result = new Uint8Array(totalLen);
  let offset = 0;
  for (const c of chunks) { result.set(c, offset); offset += c.length; }
  return result;
}
